// src/components/invoices/Invoices.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./invoices.css";
import { billPainterCss, buildBillPainterHtml } from "./billPainterHtml";
import PendingInvoices from "./PendingInvoices";
import InvoiceDetails from "./InvoiceDetails";
import { fetchBillDateRanges, fetchSavedBills, deleteBill } from "../../services/invoices";

/* -------- helpers -------- */
function safeJson(v, fallback) {
    try {
        const s = typeof v === "string" ? v.trim() : v;
        return JSON.parse(s || "");
    } catch {
        return fallback;
    }
}

function billCssText() {
    const raw = billPainterCss();
    return String(raw || "").replace(/<\/?style[^>]*>/g, "");
}

function pad2(n) {
    const s = String(n);
    return s.length === 1 ? `0${s}` : s;
}

function isoToLabel(iso) {
    try {
        const [y, m, d] = String(iso).split("-").map((x) => parseInt(x, 10));
        const dt = new Date(y, (m || 1) - 1, d || 1);
        return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return iso;
    }
}

function isoToDdMM(iso) {
    try {
        const [y, m, d] = String(iso || "").split("-");
        if (!y || !m || !d) return String(iso || "");
        return `${pad2(d)}/${pad2(m)}/${y}`;
    } catch {
        return String(iso || "");
    }
}

function moneyINR(x) {
    const n = Number(x || 0);
    const s = Math.abs(n).toFixed(2);
    return n < 0 ? `-₹${s}` : `₹${s}`;
}

function rowKey(r) {
    const kno = String(r?.kno || "").trim();
    const ps = String(r?.period_start || r?.periodStart || "").trim();
    const pe = String(r?.period_end || r?.periodEnd || "").trim();
    return `${kno}|${ps}|${pe}`;
}

/* localStorage keys */
function keyBillDatesJson(mainuser, pps) {
    return `billdates_json_${String(mainuser || "")}_${String(pps || "")}`;
}
function keyBillDatesETag(mainuser, pps) {
    return `billdates_etag_${String(mainuser || "")}_${String(pps || "")}`;
}
function keyBillsJson(mainuser, d1, d2) {
    return `savedbills_json_${String(mainuser || "")}_${d1}_${d2}`;
}
function keyBillsETag(mainuser, d1, d2) {
    return `savedbills_etag_${String(mainuser || "")}_${d1}_${d2}`;
}

function extractSavedRows(json) {
    if (!json) return [];
    if (Array.isArray(json)) return json;
    if (Array.isArray(json.rows)) return json.rows;
    if (Array.isArray(json.data)) return json.data;
    if (Array.isArray(json.bills)) return json.bills;
    if (Array.isArray(json.records)) return json.records;
    return [];
}
function hasSavedRows(json) {
    return extractSavedRows(json).length > 0;
}

/* -------- modal base -------- */
function Modal({ open, title, onClose, children, footer }) {
    if (!open) return null;
    return (
        <div className="invModalOverlay" role="dialog" aria-modal="true">
            <div className="invModalCard">
                <div className="invModalHead">
                    <div className="invModalTitle">{title}</div>
                    <button className="invModalClose" type="button" onClick={onClose}>
                        ✕
                    </button>
                </div>
                <div className="invModalBody">{children}</div>
                {footer ? <div className="invModalFoot">{footer}</div> : null}
            </div>
        </div>
    );
}

/* ✅ UPDATED: print window now uses SAME BillPainter CSS + SAME bill HTML as Preview */
/* -------- print window: use SAME BillPainter HTML/CSS as Preview -------- */
function openPrintWindow({ title, bills }) {
    const css = `
  <style>
    ${billCssText()}

    /* make print behave like preview layout */
    html, body { margin: 0; padding: 0; background: #fff; }

    /* each bill on its own page */
    .bpPrintPage { page-break-after: always; display: flex; justify-content: center; }
    .bpPrintPage:last-child { page-break-after: auto; }

    /* match preview centering */
    .bpWrap { width: fit-content !important; margin: 0 auto !important; }
    .bpBill { margin-left: auto !important; margin-right: auto !important; }

    /* prevent bottom strip wrapping in print (this is what your screenshot shows) */
    @media print {
      .bpBottomStrip { flex-wrap: nowrap !important; }
      .bpBottomStrip > div { white-space: nowrap !important; }
    }
  </style>
  `;

    const pages = (bills || [])
        .map((b) => `<div class="bpPrintPage">${b}</div>`)
        .join("");

    const html = `
  <html>
    <head>
      <meta charset="utf-8" />
      <title>${title || "Print"}</title>
      ${css}
    </head>
    <body>
      ${pages}
      <script>
        window.focus();
        setTimeout(() => window.print(), 150);
      </script>
    </body>
  </html>
  `;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
}


/* convert payload json to printable small HTML bill (kept as-is, not used for printing now) */
function billHtmlFromPayload(payloadJson, caption) {
    const p = safeJson(payloadJson || "", null) || {};
    const title = p?.title || "Invoice";
    const center = p?.center || "";
    const address = p?.address || "";
    const periodLabel = p?.period_label || "";
    const summary = p?.summary || {};
    const sankTotals = p?.sanklan?.totals || {};
    const deyArr = Array.isArray(p?.dey) ? p.dey : [];
    const kapatRows = p?.kapat?.rows || p?.kapat?.items || [];

    const deyTable =
        deyArr.length > 0
            ? `
      <table class="mini">
        <thead><tr><th>Name</th><th class="num">Amt</th></tr></thead>
        <tbody>
          ${deyArr
                .map(
                    (d) =>
                        `<tr><td>${String(d?.name || "")}</td><td class="num">${moneyINR(d?.amt || 0)}</td></tr>`
                )
                .join("")}
        </tbody>
      </table>`
            : "";

    const kapTable =
        kapatRows && kapatRows.length > 0
            ? `
      <table class="mini">
        <thead><tr><th>Name</th><th class="num">Cut</th></tr></thead>
        <tbody>
          ${kapatRows
                .map(
                    (k) =>
                        `<tr><td>${String(k?.name || "")}</td><td class="num">${moneyINR(
                            k?.cutnow ?? k?.amt ?? 0
                        )}</td></tr>`
                )
                .join("")}
        </tbody>
      </table>`
            : "";

    return `
  <div class="billCard">
    <div class="hTitle">${title}</div>
    <div class="meta">
      <div>${center}</div>
      <div>${address}</div>
      <div>${periodLabel}</div>
      ${caption ? `<div class="caption">${caption}</div>` : ""}
    </div>

    <div class="grid">
      <div class="block">
        <div class="bTitle">Milk Summary</div>
        <div class="kv">
          <div>Liter</div><div class="num">${Number(sankTotals?.liter || summary?.total_liter || 0).toFixed(2)}</div>
          <div>Rate</div><div class="num">${Number(sankTotals?.rate || summary?.avg_rate || 0).toFixed(2)}</div>
          <div>Milk Amount</div><div class="num">${moneyINR(summary?.milk_amt || sankTotals?.amt || 0)}</div>
        </div>
      </div>

      <div class="block">
        <div class="bTitle">Totals</div>
        <div class="kv">
          <div>Gross</div><div class="num">${moneyINR(summary?.gross_amt || 0)}</div>
          <div>Kapat</div><div class="num">${moneyINR(summary?.kapat_cut || 0)}</div>
          <div>Payable</div><div class="num">${moneyINR(summary?.payable || 0)}</div>
        </div>
      </div>
    </div>

    ${deyTable}
    ${kapTable}
  </div>
  `;
}

export default function Invoices({ mainuser, selectedKendra }) {
    const paymentperiodsettings =
        selectedKendra?.vikriperiodsettings || selectedKendra?.paymentperiodsettings || "01-10,11-20,21-ME";

    // view: list | pending | details
    const [view, setView] = useState("list");

    // ranges
    const [ranges, setRanges] = useState([]);
    const [rangeLoading, setRangeLoading] = useState(false);

    // selected range
    const [date1, setDate1] = useState("");
    const [date2, setDate2] = useState("");
    const [rangeLabel, setRangeLabel] = useState("");

    // rows
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState(null);
    const [loading, setLoading] = useState(false);
    const [probing, setProbing] = useState(false);
    const [err, setErr] = useState("");

    // ui
    const [search, setSearch] = useState("");
    const [pageSize, setPageSize] = useState(100);
    const [page, setPage] = useState(1);

    // selection
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());

    // dialogs
    const [previewOpen, setPreviewOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // details nav state (used for prev/next inside InvoiceDetails)
    const [detailCtx, setDetailCtx] = useState(null);
    // { mode: "saved"|"pending", list: [], index: number, range: {d1,d2} }

    // ✅ race guards
    const reqSeqRef = useRef(0);
    const activeKeyRef = useRef(""); // `${d1}|${d2}`
    const probeTokenRef = useRef(0);
    const didProbeRef = useRef(false);

    // ✅ used to re-check newest saved range after returning
    const [refreshTick, setRefreshTick] = useState(0);

    const curRangeRef = useRef({ d1: "", d2: "" });
    useEffect(() => {
        curRangeRef.current = { d1: date1, d2: date2 };
    }, [date1, date2]);

    async function loadRanges() {
        if (!mainuser) return;

        setRangeLoading(true);
        setErr("");

        try {
            // cache-first
            const cacheJson = localStorage.getItem(keyBillDatesJson(mainuser, paymentperiodsettings)) || "";
            if (cacheJson) {
                const cachedArr = safeJson(cacheJson, []);
                const parsed = (Array.isArray(cachedArr) ? cachedArr : [])
                    .map((o) => {
                        const d1 = String(o?.date1 || "").trim();
                        const d2 = String(o?.date2 || "").trim();
                        if (!d1 || !d2) return null;
                        return { date1: d1, date2: d2, label: `${isoToLabel(d1)} - ${isoToLabel(d2)}` };
                    })
                    .filter(Boolean);

                parsed.sort((a, b) =>
                    b.date1 === a.date1 ? b.date2.localeCompare(a.date2) : b.date1.localeCompare(a.date1)
                );

                if (parsed.length) setRanges(parsed);
            }

            const etag = localStorage.getItem(keyBillDatesETag(mainuser, paymentperiodsettings)) || null;
            const resp = await fetchBillDateRanges(mainuser, paymentperiodsettings, { etag });
            if (resp.status === 304) return;

            const arr = safeJson(resp.text, []);
            const parsed = (Array.isArray(arr) ? arr : [])
                .map((o) => {
                    const d1 = String(o?.date1 || "").trim();
                    const d2 = String(o?.date2 || "").trim();
                    if (!d1 || !d2) return null;
                    return { date1: d1, date2: d2, label: `${isoToLabel(d1)} - ${isoToLabel(d2)}` };
                })
                .filter(Boolean);

            parsed.sort((a, b) =>
                b.date1 === a.date1 ? b.date2.localeCompare(a.date2) : b.date1.localeCompare(a.date1)
            );

            setRanges(parsed);

            localStorage.setItem(keyBillDatesJson(mainuser, paymentperiodsettings), JSON.stringify(arr));
            if (resp.etag) localStorage.setItem(keyBillDatesETag(mainuser, paymentperiodsettings), resp.etag);
        } catch (e) {
            setErr(e?.message || "Network Error");
            setRanges([]);
        } finally {
            setRangeLoading(false);
        }
    }

    useEffect(() => {
        if (!mainuser) return;
        loadRanges();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainuser, paymentperiodsettings]);

    useEffect(() => {
        didProbeRef.current = false;
    }, [mainuser, paymentperiodsettings]);

    async function findNewestRangeWithSaved({ token }) {
        if (!mainuser) return null;
        if (!ranges.length) return null;

        const desc = [...ranges].sort((a, b) =>
            b.date1 === a.date1 ? b.date2.localeCompare(a.date2) : b.date1.localeCompare(a.date1)
        );

        for (let i = 0; i < desc.length; i++) {
            if (probeTokenRef.current !== token) return null;

            const rr = desc[i];

            // 1) cache-first
            try {
                const cachedBody = localStorage.getItem(keyBillsJson(mainuser, rr.date1, rr.date2)) || "";
                if (cachedBody) {
                    const cachedJson = safeJson(cachedBody, null);
                    if (hasSavedRows(cachedJson)) return rr;
                }
            } catch { }

            // 2) network probe (ISO)
            try {
                const resp = await fetchSavedBills(mainuser, rr.date1, rr.date2, { etag: null });
                if (probeTokenRef.current !== token) return null;

                const json = safeJson(resp.text, null);
                if (hasSavedRows(json)) {
                    try {
                        localStorage.setItem(keyBillsJson(mainuser, rr.date1, rr.date2), JSON.stringify(json));
                        if (resp.etag) localStorage.setItem(keyBillsETag(mainuser, rr.date1, rr.date2), resp.etag);
                    } catch { }
                    return rr;
                }
            } catch { }

            // 3) fallback (dd/MM)
            try {
                const resp2 = await fetchSavedBills(mainuser, isoToDdMM(rr.date1), isoToDdMM(rr.date2), { etag: null });
                if (probeTokenRef.current !== token) return null;

                const json2 = safeJson(resp2.text, null);
                if (hasSavedRows(json2)) {
                    try {
                        localStorage.setItem(keyBillsJson(mainuser, rr.date1, rr.date2), JSON.stringify(json2));
                        if (resp2.etag) localStorage.setItem(keyBillsETag(mainuser, rr.date1, rr.date2), resp2.etag);
                    } catch { }
                    return rr;
                }
            } catch { }
        }

        return null;
    }

    // ✅ initial pick: newest range that has saved bills
    useEffect(() => {
        if (!mainuser) return;
        if (!ranges.length) return;
        if (didProbeRef.current) return;

        didProbeRef.current = true;
        const token = ++probeTokenRef.current;

        (async () => {
            setProbing(true);
            setErr("");

            const chosen = await findNewestRangeWithSaved({ token });
            const finalPick = chosen || ranges[0];

            reqSeqRef.current++;
            activeKeyRef.current = `${finalPick.date1}|${finalPick.date2}`;

            setDate1(finalPick.date1);
            setDate2(finalPick.date2);
            setRangeLabel(finalPick.label);

            setProbing(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ranges, mainuser, paymentperiodsettings]);

    async function loadSavedBills(d1, d2) {
        if (!mainuser || !d1 || !d2) return;

        const mySeq = ++reqSeqRef.current;
        const myKey = `${d1}|${d2}`;
        activeKeyRef.current = myKey;

        setLoading(true);
        setErr("");

        try {
            const cachedBody = localStorage.getItem(keyBillsJson(mainuser, d1, d2)) || "";
            if (cachedBody) {
                const cached = safeJson(cachedBody, null);
                if (cached) {
                    const arr = extractSavedRows(cached);
                    if (reqSeqRef.current === mySeq && activeKeyRef.current === myKey) {
                        setRows(Array.isArray(arr) ? arr : []);
                        setTotals(cached.totals || null);
                    }
                }
            }

            const resp = await fetchSavedBills(mainuser, d1, d2, { etag: null });
            if (reqSeqRef.current !== mySeq || activeKeyRef.current !== myKey) return;

            const json = safeJson(resp.text, null);
            if (!json) {
                setRows([]);
                setTotals(null);
                return;
            }

            const arr = extractSavedRows(json);
            setRows(Array.isArray(arr) ? arr : []);
            setTotals(json.totals || null);

            localStorage.setItem(keyBillsJson(mainuser, d1, d2), JSON.stringify(json));
            if (resp.etag) localStorage.setItem(keyBillsETag(mainuser, d1, d2), resp.etag);
        } catch (e) {
            if (reqSeqRef.current !== mySeq || activeKeyRef.current !== myKey) return;
            setErr(e?.message || "Network Error");
            setRows([]);
            setTotals(null);
        } finally {
            if (reqSeqRef.current === mySeq && activeKeyRef.current === myKey) setLoading(false);
        }
    }

    useEffect(() => {
        if (!date1 || !date2) return;
        if (!mainuser) return;
        if (probing) return;
        if (view !== "list") return;

        setSelectedKeys(new Set());
        loadSavedBills(date1, date2);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date1, date2, mainuser, probing, view]);

    // ✅ when coming back from pending OR details: refresh newest saved if needed
    useEffect(() => {
        if (refreshTick <= 0) return;
        if (!mainuser) return;
        if (!ranges.length) return;

        const token = ++probeTokenRef.current;

        (async () => {
            setProbing(true);

            const chosen = await findNewestRangeWithSaved({ token });
            const cur = curRangeRef.current || { d1: "", d2: "" };

            if (chosen) {
                const newKey = `${chosen.date1}|${chosen.date2}`;
                const curKey = `${cur.d1}|${cur.d2}`;

                if (newKey !== curKey) {
                    reqSeqRef.current++;
                    activeKeyRef.current = newKey;
                    setDate1(chosen.date1);
                    setDate2(chosen.date2);
                    setRangeLabel(chosen.label);
                    setProbing(false);
                    return;
                }
            }

            if (cur.d1 && cur.d2) await loadSavedBills(cur.d1, cur.d2);
            setProbing(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTick]);

    // search
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows || [];
        return (rows || []).filter((r) => {
            const kno = String(r?.kno || "").toLowerCase();
            const cname = String(r?.cname || "").toLowerCase();
            return kno.includes(q) || cname.includes(q);
        });
    }, [rows, search]);

    // pagination
    const totalEntries = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize));
    const safePage = Math.min(page, totalPages);

    const paged = useMemo(() => {
        const start = (safePage - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, safePage, pageSize]);

    const showingFrom = totalEntries === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const showingTo = Math.min(safePage * pageSize, totalEntries);

    useEffect(() => {
        setPage(1);
    }, [search, pageSize, date1, date2]);

    // selection helpers
    const selectedCount = selectedKeys.size;
    const pageKeys = useMemo(() => paged.map(rowKey), [paged]);

    const allOnPageSelected = useMemo(() => {
        if (!pageKeys.length) return false;
        for (const k of pageKeys) if (!selectedKeys.has(k)) return false;
        return true;
    }, [pageKeys, selectedKeys]);

    const someOnPageSelected = useMemo(() => {
        for (const k of pageKeys) if (selectedKeys.has(k)) return true;
        return false;
    }, [pageKeys, selectedKeys]);

    function toggleRow(k) {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k);
            else next.add(k);
            return next;
        });
    }

    function toggleSelectAllOnPage() {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            const shouldSelect = !allOnPageSelected;
            for (const k of pageKeys) {
                if (shouldSelect) next.add(k);
                else next.delete(k);
            }
            return next;
        });
    }

    function onSelectRange(v) {
        const found = ranges.find((x) => `${x.date1}|${x.date2}` === v);
        if (!found) return;

        probeTokenRef.current++;
        reqSeqRef.current++;

        setDate1(found.date1);
        setDate2(found.date2);
        setRangeLabel(found.label);
    }

    const selectedRows = useMemo(() => {
        const map = new Map();
        for (const r of rows) map.set(rowKey(r), r);
        const out = [];
        for (const k of selectedKeys) {
            const r = map.get(k);
            if (r) out.push(r);
        }
        return out;
    }, [rows, selectedKeys]);

    function onPreview() {
        if (selectedCount <= 0) return;
        setPreviewOpen(true);
    }

    /* ✅ UPDATED: Print now uses same BillPainter HTML as Preview */
    function onPrint() {
        if (selectedCount <= 0) return;

        // ✅ print EXACTLY what Preview renders
        const bills = (bpPreviewRows || []).map((x) => x.html).filter(Boolean);

        if (!bills.length) {
            alert("No payload_json found for selected invoices.");
            return;
        }

        openPrintWindow({ title: "Invoice Print", bills });
    }


    async function confirmDelete() {
        if (!selectedRows.length || deleting) return;

        setDeleting(true);
        try {
            for (const r of selectedRows) {
                const kno = String(r?.kno || "");
                const ps = String(r?.period_start || r?.periodStart || "");
                const pe = String(r?.period_end || r?.periodEnd || "");
                if (!kno || !ps || !pe) continue;

                const resp = await deleteBill(mainuser, ps, pe, kno);
                const ok = typeof resp === "string" && resp.includes('"ok"') && resp.includes("true");
                if (!ok) console.warn("delete failed", kno, resp);
            }

            setDeleteOpen(false);
            setSelectedKeys(new Set());
            await loadSavedBills(date1, date2);
        } catch (e) {
            alert(e?.message || "Delete failed");
        } finally {
            setDeleting(false);
        }
    }

    // ✅ BillPainter preview HTML
    const bpCss = useMemo(() => billCssText(), []);
    const bpPreviewRows = useMemo(() => {
        return selectedRows
            .filter((r) => r?.payload_json || r?.payloadJson)
            .map((r) => {
                const payload = r?.payload_json || r?.payloadJson || "";
                let html = "";
                try {
                    html = buildBillPainterHtml(payload, {
                        centerName: selectedKendra?.name,
                        city: selectedKendra?.city,
                        tal: selectedKendra?.tal,
                        dist: selectedKendra?.dist,
                        owner: selectedKendra?.owner,
                        mobno: selectedKendra?.mobno,
                    });
                } catch (e) {
                    html = `<pre style="white-space:pre-wrap">${String(payload || "")}</pre>`;
                }
                return { key: rowKey(r), html };
            });
    }, [selectedRows, selectedKendra]);

    // ✅ open details from SAVED list
    function openSavedDetails(row, idxInPaged) {
        const list = filtered || [];
        const k = rowKey(row);
        const index = Math.max(
            0,
            list.findIndex((x) => rowKey(x) === k)
        );

        setDetailCtx({
            mode: "saved",
            list,
            index,
            range: {
                d1: String(row?.period_start || date1 || ""),
                d2: String(row?.period_end || date2 || ""),
            },
        });

        setView("details");
    }

    // ✅ open details from PENDING list
    function openPendingDetails({ list, index, range }) {
        setDetailCtx({
            mode: "pending",
            list: Array.isArray(list) ? list : [],
            index: Number(index || 0),
            range: range || { d1: date1, d2: date2 },
        });
        setView("details");
    }

    function onGenerateInvoice() {
        setView("pending");
    }

    // ========= RENDER: Pending =========
    if (view === "pending") {
        return (
            <PendingInvoices
                mainuser={mainuser}
                selectedKendra={selectedKendra}
                paymentperiodsettings={paymentperiodsettings}
                initialDate1={date1}
                initialDate2={date2}
                onBack={() => {
                    setView("list");
                    setRefreshTick((t) => t + 1);
                    loadRanges();
                }}
                onOpenDetails={openPendingDetails}
            />
        );
    }

    // ========= RENDER: Details =========
    if (view === "details" && detailCtx) {
        const row = detailCtx.list[detailCtx.index] || null;

        const kno = String(row?.kno || "");
        const cname = String(row?.cname || "");

        const d1 = String(detailCtx?.range?.d1 || date1 || "");
        const d2 = String(detailCtx?.range?.d2 || date2 || "");

        return (
            <InvoiceDetails
                mainuser={mainuser}
                selectedKendra={selectedKendra}
                mode={detailCtx.mode}
                source={detailCtx.mode === "saved" ? "saved" : undefined}
                kno={kno}
                cname={cname}
                date1={d1}
                date2={d2}
                list={detailCtx.list}
                index={detailCtx.index}
                onPrev={() =>
                    setDetailCtx((p) => {
                        if (!p) return p;
                        const ni = Math.max(0, p.index - 1);
                        return { ...p, index: ni };
                    })
                }
                onNext={() =>
                    setDetailCtx((p) => {
                        if (!p) return p;
                        const ni = Math.min((p.list?.length || 1) - 1, p.index + 1);
                        return { ...p, index: ni };
                    })
                }
                onBack={() => {
                    setView(detailCtx.mode === "pending" ? "pending" : "list");
                    setRefreshTick((t) => t + 1);
                }}
            />
        );
    }

    // ========= RENDER: List =========
    const emptyMessage =
        !loading && !probing && (rows || []).length === 0 && search.trim() === "" ? "No Saved Bills." : "No Records Found.";

    return (
        <div className="invPage">
            {/* Date Range row + Generate Invoice button */}
            <div
                className="invFilters"
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div className="invFilterRow">
                        <div className="invFilterLabel">Date Range :</div>
                        <select
                            className="invSelect"
                            value={date1 && date2 ? `${date1}|${date2}` : ""}
                            onChange={(e) => onSelectRange(e.target.value)}
                            disabled={rangeLoading || ranges.length === 0}
                        >
                            {ranges.length === 0 ? (
                                <option value="">{rangeLoading ? "Loading..." : "No ranges"}</option>
                            ) : (
                                ranges.map((r) => (
                                    <option key={`${r.date1}|${r.date2}`} value={`${r.date1}|${r.date2}`}>
                                        {r.label}
                                    </option>
                                ))
                            )}
                        </select>
                    </div>

                    <div className="invRangeLabel">{rangeLabel}</div>
                    {probing ? <div className="invMsg">Loading latest saved bills…</div> : null}
                </div>

                <button
                    type="button"
                    onClick={onGenerateInvoice}
                    className="invActionBtn"
                    style={{
                        padding: "10px 16px",
                        borderRadius: 10,
                        fontWeight: 700,
                        background: "#2e7d32",
                        color: "#fff",
                        border: "none",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                    }}
                >
                    Generate Invoice
                </button>
            </div>

            <div className="invCard">
                <div className="invCardHead">
                    <div className="invCardTitle">
                        Invoices <span className="invSelected">(Selected : {selectedCount})</span>
                    </div>

                    <div className="invHeadRight">
                        {selectedCount > 0 ? (
                            <div className="invSelActions">
                                <button type="button" className="invActionBtn" onClick={onPreview}>
                                    👁 Preview
                                </button>
                                <button type="button" className="invActionBtn" onClick={onPrint}>
                                    🖨 Print
                                </button>
                                <button type="button" className="invActionBtn danger" onClick={() => setDeleteOpen(true)}>
                                    🗑 Delete
                                </button>
                            </div>
                        ) : null}

                        <div className="invSearchBox">
                            <input
                                className="invSearchInput"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search Invoice"
                            />
                        </div>
                    </div>
                </div>

                {err ? <div className="invMsg err">{err}</div> : null}

                <div className="invTableWrap">
                    <table className="invTable">
                        <thead>
                            <tr>
                                <th className="chk" />
                                <th colSpan={2}>Farmer Info</th>
                                <th colSpan={1}>Milks</th>
                                <th colSpan={3}>Deduction</th>
                                <th colSpan={1}>Credit</th>
                                <th colSpan={3}>Payment</th>
                            </tr>
                            <tr>
                                <th className="chk">
                                    <input
                                        type="checkbox"
                                        checked={allOnPageSelected}
                                        ref={(el) => {
                                            if (!el) return;
                                            el.indeterminate = !allOnPageSelected && someOnPageSelected;
                                        }}
                                        onChange={toggleSelectAllOnPage}
                                    />
                                </th>
                                <th>Code</th>
                                <th>Name</th>
                                <th>Quantity</th>
                                <th>Management Charges</th>
                                <th>In Hand Advance</th>
                                <th>Total Deduction (A)</th>
                                <th>Total Credit (B)</th>
                                <th>Milk Amount (C)</th>
                                <th>Total Amount (B + C)</th>
                                <th>Balance</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading || probing ? (
                                <tr>
                                    <td colSpan={11} className="invMsg">
                                        Loading...
                                    </td>
                                </tr>
                            ) : paged.length === 0 ? (
                                <tr>
                                    <td colSpan={11} className="invNoRecords">
                                        {emptyMessage}
                                    </td>
                                </tr>
                            ) : (
                                paged.map((r, idx) => {
                                    const k = rowKey(r);
                                    const isSel = selectedKeys.has(k);

                                    const kno = String(r?.kno || "");
                                    const cname = String(r?.cname || "");

                                    const qty = Number(r?.qty || r?.liter || 0) || 0;
                                    const mgmt = Number(r?.mgmt_cut || r?.management_charges || 0) || 0;
                                    const inHand = Number(r?.in_hand_advance || 0) || 0;
                                    const totalDedA = Number(r?.total_deduction || r?.kapat_cut || 0) || 0;
                                    const totalCreditB = Number(r?.total_credit || 0) || 0;
                                    const milkAmtC = Number(r?.milk_amt || r?.gross_amt || 0) || 0;

                                    const totalBC = totalCreditB + milkAmtC;
                                    const bal = Number(r?.overall_bal || r?.balance || 0) || 0;

                                    return (
                                        <tr
                                            key={`${k}-${idx}`}
                                            className={isSel ? "rowSel" : ""}
                                            onClick={() => openSavedDetails(r, idx)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <td className="chk" onClick={(e) => e.stopPropagation()}>
                                                <input type="checkbox" checked={isSel} onChange={() => toggleRow(k)} />
                                            </td>
                                            <td>{kno}</td>
                                            <td>
                                                <b>{cname}</b>
                                            </td>
                                            <td className="num">{qty.toFixed(2)}</td>
                                            <td className="num">{moneyINR(mgmt)}</td>
                                            <td className="num">{moneyINR(inHand)}</td>
                                            <td className="num">{moneyINR(totalDedA)}</td>
                                            <td className="num">{moneyINR(totalCreditB)}</td>
                                            <td className="num">{moneyINR(milkAmtC)}</td>
                                            <td className="num">{moneyINR(totalBC)}</td>
                                            <td className={`num ${bal < 0 ? "neg" : ""}`}>{moneyINR(bal)}</td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="invFooter">
                    <div className="invShowing">
                        Showing {showingFrom} to {showingTo} of {totalEntries} entries
                    </div>

                    <div className="invPager">
                        <button type="button" className="invPgBtn" onClick={() => setPage(1)} disabled={safePage === 1}>
                            «
                        </button>
                        <button type="button" className="invPgBtn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>
                            ‹
                        </button>
                        <button type="button" className="invPgNum active">
                            {safePage}
                        </button>
                        <button
                            type="button"
                            className="invPgBtn"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={safePage === totalPages}
                        >
                            ›
                        </button>
                        <button type="button" className="invPgBtn" onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>
                            »
                        </button>

                        <select className="invPageSize" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>

                {totals ? (
                    <div className="invTotalsBar">
                        <div>
                            Avg Rate: <b>{Number(totals?.avg_rate || 0).toFixed(2)}</b>
                        </div>
                        <div>
                            Total Liter: <b>{Number(totals?.total_liter || 0).toFixed(2)}</b>
                        </div>
                        <div>
                            Total Gross: <b>{moneyINR(totals?.total_gross_amt || 0)}</b>
                        </div>
                        <div>
                            Total Kapat: <b>{moneyINR(totals?.total_kapat_cut || 0)}</b>
                        </div>
                        <div>
                            Total Payable: <b>{moneyINR(totals?.total_payable || 0)}</b>
                        </div>
                    </div>
                ) : null}
            </div>

            {/* PREVIEW MODAL */}
            <Modal
                open={previewOpen}
                title="Preview"
                onClose={() => setPreviewOpen(false)}
                footer={
                    <div className="invModalFootRight">
                        <button className="invActionBtn" type="button" onClick={onPrint}>
                            🖨 Print
                        </button>
                        <button className="invModalBtn" type="button" onClick={() => setPreviewOpen(false)}>
                            Close
                        </button>
                    </div>
                }
            >
                <style>{`
          ${bpCss}
          .bpPreviewStack{
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 18px;
            align-items: center;
            padding: 4px 0;
          }
          .bpPreviewItem{
            width: 100%;
            display: flex;
            justify-content: center;
            overflow: hidden;
          }
          .bpPreviewItem > div{
            width: fit-content;
            margin: 0 auto;
          }
          .bpPreviewItem .bpBill{
            display: block;
            margin-left: auto !important;
            margin-right: auto !important;
          }
        `}</style>

                <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <div className="bpPreviewStack">
                        {bpPreviewRows.length === 0 ? (
                            <div className="invMsg">No payload_json found for selected invoices.</div>
                        ) : (
                            bpPreviewRows.map((b) => (
                                <div key={b.key} className="bpPreviewItem" dangerouslySetInnerHTML={{ __html: b.html }} />
                            ))
                        )}
                    </div>
                </div>
            </Modal>

            {/* DELETE CONFIRM MODAL */}
            <Modal
                open={deleteOpen}
                title="Delete"
                onClose={() => (deleting ? null : setDeleteOpen(false))}
                footer={
                    <div className="invModalFootRight">
                        <button className="invModalBtn" type="button" disabled={deleting} onClick={() => setDeleteOpen(false)}>
                            Cancel
                        </button>
                        <button className="invModalBtn danger" type="button" disabled={deleting} onClick={confirmDelete}>
                            {deleting ? "Deleting..." : `Delete (${selectedCount})`}
                        </button>
                    </div>
                }
            >
                <div className="invConfirmText">
                    Are you sure you want to delete <b>{selectedCount}</b> invoice(s)?
                </div>
                <div className="invConfirmList">
                    {selectedRows.slice(0, 8).map((r) => (
                        <div key={rowKey(r)} className="invConfirmItem">
                            {String(r?.kno || "")} - {String(r?.cname || "")}
                        </div>
                    ))}
                    {selectedRows.length > 8 ? <div className="invConfirmMore">+ {selectedRows.length - 8} more…</div> : null}
                </div>
            </Modal>
        </div>
    );
}
